import { useState } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Reveal from '../components/animations/Reveal';

const FEEDBACK_TYPES = [
  'General Feedback',
  'Bug Report',
  'Feature Request',
  'Performance Issue',
];

const INITIAL = { name: '', email: '', feedbackType: 'General Feedback', message: '' };

const FeedbackPage = () => {
  const [form, setForm]       = useState(INITIAL);
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    // clear field error on change
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                                    e.name    = 'Name is required.';
    if (!form.email.trim())                                   e.email   = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email   = 'Enter a valid email address.';
    if (!form.message.trim())                                 e.message = 'Message is required.';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setStatus('submitting');
    setServerError('');

    try {
      const res = await fetch('/api/feedback', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Something went wrong.');
      }

      setStatus('success');
      setForm(INITIAL);
    } catch (err) {
      setStatus('error');
      setServerError(err.message || 'Failed to send feedback. Please try again.');
    }
  };

  const inputClass = (field) =>
    `input-anim w-full bg-slate-50 border rounded-xl p-3 text-slate-800 placeholder-slate-400 focus:outline-none transition-all ${
      errors[field]
        ? 'border-red-400 focus:border-red-500'
        : 'border-slate-300 focus:border-brand'
    }`;

  return (
    <div className="py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">Feedback</h2>
            <p className="text-xl text-slate-600">Help us improve CryMiner with your valuable feedback</p>
          </div>
        </Reveal>

        {/* ── Success state ───────────────────────────────────────────────── */}
        {status === 'success' && (
          <Reveal>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-semibold text-green-800">Feedback sent!</p>
              <p className="text-sm text-green-700 mt-1">
                Thank you for reaching out. We'll review your message and get back to you if needed.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-5 text-sm text-brand hover:underline"
              >
                Send another message
              </button>
            </div>
          </Reveal>
        )}

        {/* ── Form ────────────────────────────────────────────────────────── */}
        {status !== 'success' && (
          <Reveal delay={100}>
            <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-md">
              <div className="space-y-6">

                {/* Name + Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update('name')}
                      className={inputClass('name')}
                      placeholder="Your name"
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      className={inputClass('email')}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Feedback Type */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Feedback Type</label>
                  <select
                    value={form.feedbackType}
                    onChange={update('feedbackType')}
                    className="input-anim w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-800 focus:border-brand focus:outline-none transition-all"
                  >
                    {FEEDBACK_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Message</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={update('message')}
                    className={inputClass('message')}
                    placeholder="Please share your feedback, suggestions, or report any issues..."
                  />
                  {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
                </div>

                {/* Server error */}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {serverError}
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={status === 'submitting'}
                  className="brand-btn w-full px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {status === 'submitting' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {status === 'submitting' ? 'Sending...' : 'Submit Feedback'}
                </button>

              </div>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;